/* App shell + path router */
const { useState, useEffect } = React;

const DEFAULT_SITE_NAME = "Parcours d'Artistes";

function getSiteOrigin(){
  const envOrigin = (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_SITE_URL)
    ? String(import.meta.env.VITE_SITE_URL).trim()
    : "";

  if (envOrigin) {
    return envOrigin.replace(/\/$/, "");
  }

  return window.location.origin;
}

function toAbsoluteUrl(pathOrUrl, origin){
  if (!pathOrUrl) return `${origin}/assets/hero-landing.png`;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${origin}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

function clip(text, max = 160){
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, Math.max(0, max - 1)).trim()}…`;
}

function upsertMeta(selector, attr, value){
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(attr, selector.match(/\[(?:name|property)=\"([^\"]+)\"\]/)?.[1] || "");
    document.head.appendChild(node);
  }
  node.setAttribute("content", value);
}

function upsertLinkCanonical(url){
  let node = document.head.querySelector('link[rel="canonical"]');
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", "canonical");
    document.head.appendChild(node);
  }
  node.setAttribute("href", url);
}

function upsertJsonLd(payload){
  const id = "seo-jsonld";
  let node = document.head.querySelector(`#${id}`);
  if (!node) {
    node = document.createElement("script");
    node.id = id;
    node.type = "application/ld+json";
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify(payload);
}

function buildSeo(route, parcoursData, siteOrigin){
  const fullUrl = `${siteOrigin}${route === "/" ? "" : route}`;
  const heroImage = toAbsoluteUrl("/assets/hero-landing.png", siteOrigin);

  if (route === "/" || route === "") {
    return {
      title: "Parcours d'Artistes 2026 en Belgique | Mozaika",
      description: "Découvrez les parcours d'artistes en Belgique. Carte interactive, lieux d'exposition, artistes et informations pratiques pour préparer votre visite.",
      canonical: `${siteOrigin}/`,
      image: heroImage,
      type: "website",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: DEFAULT_SITE_NAME,
        url: `${siteOrigin}/`,
        inLanguage: "fr-BE"
      }
    };
  }

  if (route === "/parcours") {
    return {
      title: `Tous les parcours 2026 | ${DEFAULT_SITE_NAME}`,
      description: `Consultez la liste complète des parcours d'artistes 2026 en Belgique: ${parcoursData.length} parcours, cartes, artistes et lieux à visiter.`,
      canonical: fullUrl,
      image: heroImage,
      type: "website",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Liste des parcours d'artistes 2026",
        url: fullUrl,
        inLanguage: "fr-BE",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: parcoursData.length,
          itemListElement: parcoursData.map((p, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${siteOrigin}/parcours/${p.slug}`,
            name: p.title
          }))
        }
      }
    };
  }

  const detailMatch = route.match(/^\/parcours\/([^/]+)$/);
  if (detailMatch) {
    const slug = detailMatch[1];
    const p = parcoursData.find((entry) => entry.slug === slug);
    if (p) {
      const detailUrl = `${siteOrigin}/parcours/${p.slug}`;
      const detailDescription = clip(p.description || p.summary || "Parcours d'artistes en Belgique.", 170);
      return {
        title: `${p.title} | ${DEFAULT_SITE_NAME}`,
        description: detailDescription,
        canonical: detailUrl,
        image: toAbsoluteUrl(p.image || "/assets/hero-landing.png", siteOrigin),
        type: "article",
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: p.title,
          description: detailDescription,
          url: detailUrl,
          image: toAbsoluteUrl(p.image || "/assets/hero-landing.png", siteOrigin),
          inLanguage: "fr-BE",
          isPartOf: {
            "@type": "WebSite",
            name: DEFAULT_SITE_NAME,
            url: `${siteOrigin}/`
          },
          creator: p.organizer || "Mozaika"
        }
      };
    }
  }

  return {
    title: `${DEFAULT_SITE_NAME} | Plateforme officielle`,
    description: "Découvrez les parcours d'artistes en Belgique.",
    canonical: fullUrl,
    image: heroImage,
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: DEFAULT_SITE_NAME,
      url: fullUrl,
      inLanguage: "fr-BE"
    }
  };
}

function applySeo(seo){
  document.title = seo.title;
  upsertMeta('meta[name="description"]', "name", seo.description);
  upsertMeta('meta[name="robots"]', "name", "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1");

  upsertMeta('meta[property="og:type"]', "property", seo.type || "website");
  upsertMeta('meta[property="og:site_name"]', "property", DEFAULT_SITE_NAME);
  upsertMeta('meta[property="og:locale"]', "property", "fr_BE");
  upsertMeta('meta[property="og:title"]', "property", seo.title);
  upsertMeta('meta[property="og:description"]', "property", seo.description);
  upsertMeta('meta[property="og:url"]', "property", seo.canonical);
  upsertMeta('meta[property="og:image"]', "property", seo.image);

  upsertMeta('meta[name="twitter:card"]', "name", "summary_large_image");
  upsertMeta('meta[name="twitter:title"]', "name", seo.title);
  upsertMeta('meta[name="twitter:description"]', "name", seo.description);
  upsertMeta('meta[name="twitter:image"]', "name", seo.image);
  upsertMeta('meta[name="twitter:image:alt"]', "name", seo.title);

  upsertLinkCanonical(seo.canonical);
  upsertJsonLd(seo.jsonLd);
}

function usePathRoute(){
  const [route, setRoute] = useState(window.location.pathname || "/");
  useEffect(() => {
    const onPopState = () => {
      setRoute(window.location.pathname || "/");
      window.scrollTo({top:0, behavior:"instant"});
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  return route;
}

function App(){
  const route = usePathRoute();
  const D = window.PA_DATA;
  const [t, setTweak] = useTweaks(/*EDITMODE-BEGIN*/{
    "palette": "Terre cuite",
    "density": "Aéré",
    "titleFont": "Instrument Serif",
    "heroVariant": "Mots peints"
  }/*EDITMODE-END*/);

  // Apply density to body
  useEffect(() => {
    document.body.classList.remove("density-cozy","density-tight");
    document.body.classList.add(t.density === "Compact" ? "density-tight" : "density-cozy");
  }, [t.density]);

  // Apply palette tokens
  useEffect(() => {
    const root = document.documentElement;
    const presets = {
      "Terre cuite": { terracotta:"#C5694A", ochre:"#D4A53C", sage:"#7E9579", slate:"#3F5F86", brick:"#C4837A" },
      "Bord de mer": { terracotta:"#5B8DBE", ochre:"#E8C474", sage:"#94B3A8", slate:"#2F4858", brick:"#D08C7B" },
      "Forêt":       { terracotta:"#A56A43", ochre:"#C2A24C", sage:"#5E7858", slate:"#3D5641", brick:"#9B6E5F" }
    };
    const p = presets[t.palette] || presets["Terre cuite"];
    Object.entries(p).forEach(([k,v]) => root.style.setProperty(`--${k}`, v));
  }, [t.palette]);

  // Apply title font
  useEffect(() => {
    const fonts = {
      "Instrument Serif": '"Instrument Serif", Georgia, serif',
      "DM Serif Display": '"DM Serif Display", Georgia, serif',
      "Playfair":         '"Playfair Display", Georgia, serif'
    };
    document.documentElement.style.setProperty("--font-serif", fonts[t.titleFont] || fonts["Instrument Serif"]);
  }, [t.titleFont]);

  // Inject additional google fonts when needed
  useEffect(() => {
    const id = "extra-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id; link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Playfair+Display:ital,wght@0,400;1,400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  // Parse the route
  const parts = route.split("/").filter(Boolean);
  const root = parts[0] || "";
  const showNav = root !== "admin" && root !== "organization";

  let page = null;
  if (route === "/" || root === "") {
    page = <LandingPage t={t} setTweak={setTweak}/>;
  } else if (root === "parcours" && parts[1]) {
    page = <ParcoursDetailPage slug={parts[1]}/>;
  } else if (root === "parcours") {
    page = <ParcoursListPage/>;
  } else if (root === "explore") {
    page = <ParcoursListPage/>;
  } else if (root === "admin") {
    page = <AdminPage/>;
  } else if (root === "organization") {
    page = <OrganizationPage/>;
  } else {
    page = <LandingPage t={t} setTweak={setTweak}/>;
  }

  useEffect(() => {
    const siteOrigin = getSiteOrigin();
    const canonicalRoute = root === "parcours" && parts[1] ? `/parcours/${parts[1]}` : (root === "parcours" ? "/parcours" : route || "/");
    const seo = buildSeo(canonicalRoute, D.parcours || [], siteOrigin);
    applySeo(seo);
  }, [route, root, parts, D]);

  return (
    <>
      {showNav && <Navbar current={route} transparent={route === "/"} />}
      <main>{page}</main>
      {showNav && <Footer/>}
      <ArtistModalHost/>
      <TweaksPanel title="Tweaks">
        <TweakSection title="Palette">
          <TweakRadio
            label="Accents"
            value={t.palette}
            options={["Terre cuite","Forêt","Bord de mer"]}
            onChange={(v) => setTweak("palette", v)}
          />
        </TweakSection>
        <TweakSection title="Typographie">
          <TweakSelect
            label="Police titres"
            value={t.titleFont}
            options={["Instrument Serif","DM Serif Display","Playfair"]}
            onChange={(v) => setTweak("titleFont", v)}
          />
        </TweakSection>
        <TweakSection title="Densité">
          <TweakRadio
            label="Espacements"
            value={t.density}
            options={["Aéré","Compact"]}
            onChange={(v) => setTweak("density", v)}
          />
        </TweakSection>
        <TweakSection title="Variantes">
          <TweakSelect
            label="Hero landing"
            value={t.heroVariant}
            options={["Mots peints","Photo plein cadre","Carte abstraite"]}
            onChange={(v) => setTweak("heroVariant", v)}
          />
        </TweakSection>
      </TweaksPanel>
    </>
  );
}

window.__LegacyApp = App;
